import React, { useState } from "react";

// @material-ui/core components
import { makeStyles } from "tss-react/mui";

// core components
import Image from "next/image.js";
import Link from "next/link.js";
import Button from "../CustomButtons/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsis, faCircle, faCircleExclamation } from "@fortawesome/free-solid-svg-icons"

// sections for this page
import ForumsOptionsMenu from "../ForumsOptionsMenu/ForumsOptionsMenu.js";
import ForumsTopicReplies from "../ForumsTopicReplies/ForumsTopicReplies.js";
import ForumsTopicAddReply from "../ForumsTopicAddReply/ForumsTopicAddReply.js";
import ForumsTopicEditPost from "../ForumsTopicEditPost/ForumsTopicEditPost.js";
import ForumsTopicEditReply from "../ForumsTopicEditReply/ForumsTopicEditReply.js";

import styles from "../../styles/jss/nextjs-material-kit/pages/forumsStyle.js";

// Placeholder img
import userPlaceholder from "../../public/img/no_profile.png";

// local assets
import lockIcon from "../../public/img/forums/icons/lockIcon.png";
import replyIcon from "../../public/img/forums/icons/replyIconBlue.png";
import upvoteFilledIcon from "../../public/img/forums/icons/upvoteFilledIconBlue.png";
import apprenticePinIcon from "../../public/img/forums/icons/apprenticePinIcon.png";
import upvoteIcon from "../../public/img/forums/icons/upvoteIconBlue.png";
import {SanitizeHTML} from "../../util/string";
import {useForumsLikeCommentRequest, useForumsLikeTopicRequest} from "../../request/api";
import {useForumContext} from "../../contexts/forum";
import {CommentProvider} from "../../contexts/comment";
import {useTopicContext} from "../../contexts/topic";
import * as PropTypes from "prop-types";
import {useAuthContext} from "../../contexts/auth";
import {generateTimeAgo} from "../../util/date";
import {useTopicListContext} from "../../contexts/topicList";
import {faCircleNotch} from "@fortawesome/free-solid-svg-icons/faCircleNotch";
import {buildSlug, updateQueryString} from "../../util/url";
import {useRouter} from "next/router";

const useStyles = makeStyles()(styles);

function ReplyButton(props) {
  const { classes, cx } = useStyles();

  if (!props.postView) {
      return null;
  }

  if (props.threadClosed) {
      if (props.type === "topic") {
          return (
              <div className={props.classes.threadClosedButtons}>
                  <p>Thread closed</p>
                  <Button className={cx(props.classes.darkButton + " " + props.classes.newTopicButton)} disabled>
                      <Image
                          className={props.classes.icon}
                          src={lockIcon}
                          alt={"lock-icon"}
                          width={20}
                          height={20}
                      />
                      Reply
                  </Button>
              </div>
          )
      }

      return null;
  }

  return (
    <>
      <Button
        className={cx(props.classes.darkButton + " " + props.classes.newTopicButton)}
        onClick={props.onClick}
      >
        Reply
      </Button>
    </>
  );
}

function TopicContent(props) {
  if (props.postView) {
    if (props.replyEditorOpen) {
      return (
        <ForumsTopicEditReply
          open={props.replyEditorOpen}
          onClose={props.onClose}
          topic={props.topic}
          setEdited={props.edited}
        />
      );
    }
    
    return (
      <div>
        <h1 className={props.classes.topicTitle}>
          {props.topic.topicTitle}
        </h1>
        <div>
          <SanitizeHTML html={props.topic.content} className={props.classes.topicDescription}/>
        </div>
      </div>
    );
  }


  return (
    <>
      <Link
        href={`/community/${buildSlug(props.community)}-${props.topic.subCatId}/topic/${buildSlug(props.topic.topicTitle)}-${props.topic.topicId}`}
        className={props.classes.topicLink}
      >
        <h1 className={props.classes.topicTitle}>{props.topic.topicTitle}</h1>
        <div>
          <SanitizeHTML html={props.topic.content} className={props.classes.topicDescriptionClipped}/>
        </div>
      </Link>
    </>
  );
}

export default function ForumsTopicCard(props) {
  const { auth } = useAuthContext();
  const router = useRouter();

  const { classes, cx } = useStyles();
  const topicOrComment = props.topic;
  const type = props.type ?? "topic";
  const reload = props.reload;
  const replies = props.replies;
  const postView = props.postView;
  const community = props.community ?? 'cat';
  const threadClosed = props.topic?.isLocked === true || props.parentThreadClosed === true ? true : false;
  const postDate =
    props.initial === true
      ? topicOrComment?.createdDate
      : topicOrComment?.createdDate.date;
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [replyEditorOpen, setReplyEditorOpen] = useState(false);
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [repliesExpanded, setRepliesExpanded] = useState(!!props.initial);

  const {
    isMutating: isLiking,
    trigger: likeTopic,
    error: likeError,
  } = type === 'topic' ? useForumsLikeTopicRequest({ topicId: topicOrComment?.topicId }, undefined, {
    withCredentials: true,
  }) : useForumsLikeCommentRequest({ commentId: topicOrComment?.commentId }, undefined, {
    withCredentials: true
  });;

  //TODO integrate these with API
  const [edited, setEdited] = useState(false);

  const handleUpvote = async () => {
    if (auth.user == null) {
      await router.push(updateQueryString(updateQueryString(router.asPath, "login", null), "redirectUrl", router.asPath));
    }
    else {
      await likeTopic();
      await reload();
    }
  };

  const timeAgo = generateTimeAgo(postDate);

  const handleOptionsOpen = (event) => {
    setOptionsOpen(true);
    setAnchorEl(event.currentTarget);
  };
  const handleOptionsClose = () => {
    setOptionsOpen(false);
    setAnchorEl(null);
  };

  const closeEditor = () => {
    setReplyEditorOpen(false);
  };

  const openReplyModal = async () => {
    if (auth.user == null) {
      await router.push(updateQueryString(updateQueryString(router.asPath, "login", null), "redirectUrl", router.asPath));
    } else {
      setRepliesExpanded(true);
      setReplyModalOpen(true);
    }
  };

  const closeReplyModal = () => {
    setReplyModalOpen(false);
  };

  const handleCommentClick = async () => {
    if (!props.postView) {
      router.push(`/community/${buildSlug(community)}-${topicOrComment?.subCatId}/topic/${buildSlug(topicOrComment.topicTitle)}-${topicOrComment?.topicId}`)
    }
    else if (auth.user == null) {
      await router.push(updateQueryString(updateQueryString(router.asPath, "login", null), "redirectUrl", router.asPath));
    }
    else {
      setRepliesExpanded(!repliesExpanded);
    }
  }

  return (
    <>
      <div className={classes.topicCard + (topicOrComment?.isExpert ? (" " + classes.apprenticeHighlightBorder) : "")}>
        <div className={classes.topicUserHeader}>
          <div className={classes.topicUserInfo}>
            <Image
              className={classes.userProfilePic}
              src={topicOrComment?.createdBy.image || userPlaceholder}
              alt={"user-profile-image"}
              width={30}
              height={30}
            />
            <p className={classes.userName}>
              {topicOrComment?.createdBy.username}
            </p>
            <FontAwesomeIcon icon={faCircle} className={classes.userStatus} />
            <p className={classes.postTime}>{timeAgo}</p>
            {edited && <p className={classes.editedText}>Edited</p>}
            {topicOrComment?.isExpert && 
            <div className={classes.apprenticeIcons}>
              <p className={classes.apprenticeText}>Apprentice</p>
              <Image className={classes.apprenticePinIcon} src={apprenticePinIcon} alt={"apprentice-pin-icon"} width={12} height={12} />
            </div>
            }
            {!topicOrComment?.isExpert && topicOrComment?.isSticky &&
              <div className={classes.apprenticeIcons}>
                <Image className={classes.apprenticePinIcon} src={apprenticePinIcon} alt={"apprentice-pin-icon"} width={12} height={12} />
              </div>
            }
          </div>
          <Button
            onClick={handleOptionsOpen}
            className={cx(classes.transparentButton + " " + classes.smallButton)}
          >
            <FontAwesomeIcon
              icon={faEllipsis}
              className={classes.optionsIcon}
            />
          </Button>
          <ForumsOptionsMenu
            type={type}
            open={optionsOpen}
            onClose={handleOptionsClose}
            ownPost={
              auth.user?.id === topicOrComment?.createdBy.id ? true : false
            }
            community={community}
            isAuthenticated={!!auth.user?.id}
            locked={topicOrComment?.isLocked}
            topic={topicOrComment}
            setEdited={setEdited}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            replyEditorOpen={replyEditorOpen}
            setReplyEditorOpen={setReplyEditorOpen}
            initial={props.initial}
          />
        </div>
        <TopicContent
          postView={postView}
          replyEditorOpen={replyEditorOpen}
          onClose={closeEditor}
          topic={topicOrComment}
          edited={setEdited}
          classes={classes}
          community={community}
        />
        <div className={classes.topicTagsContainer}>
          {topicOrComment?.tags.map((tag, index) => {
            return (
              <p className={classes.topicTag} key={index}>
                {tag}
              </p>
            );
          })}
        </div>
        {!replyEditorOpen && (
          <div className={classes.topicCardFooter}>
            <div className={classes.topicLikesReplies}>
              {isLiking ? <>
                <FontAwesomeIcon
                  className={classes.loadingIcon}
                  icon={faCircleNotch}
                  spin={true}
                />
              </> : <>
                <Button className={cx(classes.transparentButton + " " + classes.upvoteButton)} onClick={handleUpvote}>
                  <Image
                    className={classes.icon}
                    src={topicOrComment?.isLiked ? upvoteFilledIcon : upvoteIcon}
                    alt={"Upvote post"}
                    width={22}
                    height={22}
                  />
                </Button>
              </>}
              <p className={classes.count}>{topicOrComment?.likes}</p>
              <Button
                onClick={handleCommentClick}
                className={cx(classes.transparentButton + " " + classes.upvoteButton)}
              >
                <Image
                  className={classes.icon}
                  src={replyIcon}
                  alt={"reply-icon"}
                  width={20}
                  height={20}
                />
              </Button>
              <p className={classes.count}>{topicOrComment?.replyCount}</p>
            </div>
            <ReplyButton
              postView={postView}
              threadClosed={threadClosed}
              type={type}
              classes={classes}
              onClick={openReplyModal}
            />
          </div>
        )}
      </div>
      <CommentProvider
        topicId={topicOrComment?.topicId}
        commentId={topicOrComment?.commentId}
        shouldFetch={
          topicOrComment?.topicId &&
          topicOrComment?.commentId &&
          (replies ?? []).length === 0 &&
          repliesExpanded
        }
      >
        <ForumsTopicAddReply
          open={replyModalOpen}
          onClose={closeReplyModal}
          topic={topicOrComment}
          community={props.community}
        />
        {postView && (
          <>
            {!!threadClosed && type === "topic" && (
              <div className={classes.threadClosed}>
                <FontAwesomeIcon
                  icon={faCircleExclamation}
                  className={classes.threadClosedIcon}
                />
                <p className={classes.threadClosedText}>
                  Moderator has closed this thread, you can no longer post
                  replies.
                </p>
              </div>
            )}
            <ForumsTopicReplies
              data={topicOrComment}
              community={community}
              replies={replies}
              repliesExpanded={repliesExpanded}
              setRepliesExpanded={setRepliesExpanded}
              reload={reload}
              threadClosed={threadClosed}
              showLoginBarrier={type === "topic" && !auth.user}
              initial={props.initial}
            />
          </>
        )}
      </CommentProvider>
    </>
  );
}

